<template>
  <nav class="enhanced-navbar" :class="{
    'is-scrolled': isScrolled,
    'is-mobile': isMobile
  }" role="navigation" aria-label="main navigation">

    <!-- デスクトップナビゲーション -->
    <NavbarDesktop v-if="!isMobile" :navigation-items="navigationItems" :user="user" :is-logged-in="isLoggedIn"
      @navigate="handleNavigate" />

    <!-- モバイルナビゲーション -->
    <NavbarMobile v-else :is-menu-open="isMobileMenuOpen" @toggle-menu="toggleMobileMenu" />

    <!-- モバイルドロワー -->
    <Teleport to="body">
      <Transition name="drawer">
        <MobileDrawer v-if="isMobile && isMobileMenuOpen" :navigation-items="navigationItems" :user="user"
          :is-logged-in="isLoggedIn" @close="closeMobileMenu" @navigate="handleNavigate" />
      </Transition>
    </Teleport>

    <!-- モバイルオーバーレイ -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="isMobile && isMobileMenuOpen" class="mobile-overlay" @click="closeMobileMenu"
          :style="mobileOverlayStyle"></div>
      </Transition>
    </Teleport>
  </nav>
</template>

<script setup>
import { computed, onMounted, watch, provide, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useNavbarState } from '@/composables/useNavbarState'
import { useGlassmorphism } from '@/composables/useGlassmorphism'
import { defineAsyncComponent } from 'vue'

// 子コンポーネントの動的インポート
const NavbarDesktop = defineAsyncComponent(() => import('./NavbarDesktop.vue'))
const NavbarMobile = defineAsyncComponent(() => import('./NavbarMobile.vue'))
const MobileDrawer = defineAsyncComponent(() => import('./MobileDrawer.vue'))

// ストアとコンポーザブル
const store = useStore()
const {
  isScrolled,
  isMobileMenuOpen,
  toggleMobileMenu,
  closeMobileMenu,
  navigateTo
} = useNavbarState()

const {
  glassmorphismStyle,
  scrolledGlassmorphismStyle,
  mobileOverlayStyle,
  prefersReducedMotion
} = useGlassmorphism()

// レスポンシブ判定
const isMobile = computed(() => {
  // Vue3のグローバルプロパティからアクセス
  const app = getCurrentInstance()
  if (app && app.appContext.config.globalProperties.$isMobile) {
    return app.appContext.config.globalProperties.$isMobile()
  }
  // フォールバック
  return window.innerWidth < 1024
})

// ユーザー情報
const user = computed(() => store.state.user)
const isLoggedIn = computed(() => store.state.isLoggedIn)

// ナビゲーション項目
const navigationItems = computed(() => {
  return isMobile.value
    ? store.getters['navbar/mobileNavigationItems']
    : store.getters['navbar/desktopNavigationItems']
})

// 現在のglassmorphismスタイル
const currentGlassmorphismStyle = computed(() => {
  return isScrolled.value ? scrolledGlassmorphismStyle.value : glassmorphismStyle.value
})

// ナビゲーション処理
const handleNavigate = (path) => {
  navigateTo(path)
}

// 子コンポーネントに提供
provide('prefersReducedMotion', prefersReducedMotion)
provide('isMobile', isMobile)

// glassmorphismスタイルの適用は削除（SCSSで直接適用するため）
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/glassmorphism';

.enhanced-navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 40;
  height: var(--navbar-height);
  will-change: backdrop-filter, transform;
  transform: translateZ(0);
  transition: var(--navbar-item-transition);

  @include glassmorphism-base();

  &.is-mobile {
    height: var(--navbar-mobile-height);
  }

  &.is-scrolled {
    box-shadow: var(--navbar-shadow);
    @include glassmorphism-scrolled();
  }
}

// モバイルオーバーレイ
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 35;
  -webkit-tap-highlight-color: transparent;
}

// アニメーション
.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.drawer-enter-from {
  transform: translateX(-100%);
}

.drawer-leave-to {
  transform: translateX(-100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// prefers-reduced-motionの対応
@media (prefers-reduced-motion: reduce) {

  .enhanced-navbar,
  .drawer-enter-active,
  .drawer-leave-active,
  .fade-enter-active,
  .fade-leave-active {
    transition: none !important;
  }
}
</style>